// Shared charter-week column model for the per-boat weekly heatmaps (occupancy
// + owner). Columns are consecutive charter weeks; column 0 is the Saturday-based
// week containing Jan 1 of the selected year. Boundary weeks are clamped into
// [0, WEEK_COUNT-1] rather than dropped, so the grid never silently omits the
// week straddling Jan 1 / Dec 31.
//
// Anchored on Saturday because that is when charters change over. On ISO Mondays
// a single Sat→Fri booking was split 2/5 across two columns, so its bulk landed
// in the week starting two days after it began and a two-day ghost appeared in
// the week before — showing a boat as booked in a week it was free (#206).

export const WEEK_COUNT = 53;
const DAY_MS = 86_400_000;

/** Saturday (UTC, ms) that starts the charter week containing the given UTC ms. */
function saturdayOfUTC(ms: number): number {
  const dow = new Date(ms).getUTCDay(); // 0=Sun .. 6=Sat
  return ms - ((dow + 1) % 7) * DAY_MS;
}

/** Saturday (UTC, ms) of the charter week containing Jan 1 of `year` — column 0. */
function columnBase(year: number): number {
  return saturdayOfUTC(Date.UTC(year, 0, 1));
}

/** Column for any UTC ms date, keyed by its charter-week Saturday. Clamped to range. */
export function columnForDateMs(ms: number, year: number): number {
  const col = Math.round((saturdayOfUTC(ms) - columnBase(year)) / (7 * DAY_MS));
  return Math.min(WEEK_COUNT - 1, Math.max(0, col));
}

/** Column for a cell's charter-week Saturday ('YYYY-MM-DD'); null only if unparseable. */
export function columnIndexFor(weekStart: string, year: number): number | null {
  const ms = Date.parse(`${weekStart}T00:00:00Z`);
  if (Number.isNaN(ms)) return null;
  return columnForDateMs(ms, year);
}

/** Saturday ('YYYY-MM-DD') that starts column `col`. */
export function weekStartForColumn(year: number, col: number): string {
  return new Date(columnBase(year) + col * 7 * DAY_MS).toISOString().slice(0, 10);
}

/** Midpoint (Tuesday, 'YYYY-MM-DD') of column `col` — representative day for
 *  season-membership checks so boundary columns aren't mis-marked. */
export function weekMidForColumn(year: number, col: number): string {
  return new Date(columnBase(year) + (col * 7 + 3) * DAY_MS).toISOString().slice(0, 10);
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A charter week as people write it, Saturday to the next Saturday:
 * "03.10. – 10.10.2026", or "26.12.2026 – 02.01.2027" across New Year (#274).
 * `weekStart` is the week's Saturday, 'YYYY-MM-DD'.
 */
export function formatCharterWeekRange(weekStart: string): string {
  const start = new Date(`${weekStart}T00:00:00Z`);
  const end = new Date(start.getTime() + 7 * DAY_MS);
  const day = (d: Date) => `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.`;
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();
  return startYear === endYear
    ? `${day(start)} – ${day(end)}${endYear}`
    : `${day(start)}${startYear} – ${day(end)}${endYear}`;
}
